import { z } from 'zod';
import {
  NpAtobaraiRegistTransactionPostRequestAuthoriOptionUserOptionEnum,
  NpAtobaraiRegistTransactionPostRequestCustomerIdentificationStatusEnum,
  NpAtobaraiRegistTransactionPostRequestCustomerMembershipFlagEnum,
  NpAtobaraiRegistTransactionPostRequestPaymentBillTypeOptionBillTypeEnum,
  NpAtobaraiRegistTransactionPostRequestSalesFormEnum,
  NpAtobaraiRegistTransactionPostRequestTransactionOptionsEnum,
} from '@netprotections/berlin-bff';
import {
  str,
  num,
  numericalEnum,
  numericalStringEnum,
  allowEmpty,
  excludeEmptyArray,
} from '@schemas/common-schemas/schema-utils';
import { merchandiseCategory } from '@schemas/common-schemas/atone-common-schema';

export const npAtobaraiCustomerSchema = z
  .object({
    customer_name: str /* 必須 */,
    customer_name_kana: allowEmpty(str),
    company_name: allowEmpty(str),
    department: allowEmpty(str),
    zip_code: str /* 必須 */,
    address: str /* 必須 */,
    tel: str /* 必須 */,
    email: allowEmpty(str),
    last_purchase_date: allowEmpty(str),
    last_purchase_amount: allowEmpty(num),
    total_purchase_amount: allowEmpty(num),
    total_purchase_count: allowEmpty(num),
    subscription_count: allowEmpty(num),
    identification_status: allowEmpty(
      excludeEmptyArray(numericalEnum(NpAtobaraiRegistTransactionPostRequestCustomerIdentificationStatusEnum)),
    ),
    membership_flag: allowEmpty(numericalEnum(NpAtobaraiRegistTransactionPostRequestCustomerMembershipFlagEnum)),
    shop_customer_id: allowEmpty(str),
    membership_start_date: allowEmpty(str),
    membership_rank: allowEmpty(str),
    terminal_id: allowEmpty(str),
  })
  .passthrough();

export const npAtobaraiDestCustomerSchema = z
  .object({
    dest_customer_name: str /* 必須 */,
    dest_customer_name_kana: allowEmpty(str),
    dest_company_name: allowEmpty(str),
    dest_department: allowEmpty(str),
    dest_zip_code: str /* 必須 */,
    dest_address: str /* 必須 */,
    dest_tel: str /* 必須 */,
  })
  .passthrough();

export const npAtobaraiAuthoriOption = z
  .object({
    user_option: allowEmpty(numericalStringEnum(NpAtobaraiRegistTransactionPostRequestAuthoriOptionUserOptionEnum)),
  })
  .passthrough();

export const npAtobaraiBillOption = z
  .object({
    bill_type: numericalStringEnum(NpAtobaraiRegistTransactionPostRequestPaymentBillTypeOptionBillTypeEnum),
  })
  .passthrough();

export const npAtobaraiItemSchema = z
  .object({
    item_name: str,
    item_price: num,
    item_count: num,
    merchandise_category: allowEmpty(merchandiseCategory),
    brand_name: allowEmpty(str),
  })
  .passthrough();

export const npAtobaraiPaymentSchema = z
  .object({
    checksum: str /* 必須 */,
    shop_transaction_no: str /* 必須 */,
    transaction_options: allowEmpty(
      excludeEmptyArray(numericalStringEnum(NpAtobaraiRegistTransactionPostRequestTransactionOptionsEnum)),
    ),
    first_transaction_id: allowEmpty(str) /* △ 仕様書にない */,
    amount: num /* 必須 */,
    source_media: allowEmpty(str),
    sales_form: allowEmpty(numericalEnum(NpAtobaraiRegistTransactionPostRequestSalesFormEnum)),
    site_name: allowEmpty(str),
    site_url: allowEmpty(str),
    authori_option: allowEmpty(npAtobaraiAuthoriOption),
    bill_option: npAtobaraiBillOption /* 必須 */,
    customer: npAtobaraiCustomerSchema /* 必須 */,
    dest_customers: allowEmpty(excludeEmptyArray(npAtobaraiDestCustomerSchema)),
    items: excludeEmptyArray(npAtobaraiItemSchema) /* 必須 */,
  })
  .passthrough();

/**
 * NP後払い決済サービスのサービス分類子の実際のチェック項目。
 *
 * これを持ってして、どのサービスのパラメータかをまず判別する。
 */
const npAtobaraiSettlementServiceRawParameterClassifierSchema = z.object({
  service_type: z.literal('02'),
  modal_mode: z.literal('01').optional(),
});

/**
 * コールバック以外のServiceパラメータのバリデータ。
 *
 * postMessage経由で渡されたクライアントからの入力データは、たとえば数値型なら `1` だったり `"1"` だったり型的に不安定な入力を許容する仕様となっている。\
 * しかし、モーダル内部では型を厳密に扱うため、zodバリデータを通してチェックし、期待した型情報への正規化を行ってから利用することになっている。
 */
export const npAtobaraiSettlementServiceParameterValidator = z
  .object({
    ...npAtobaraiSettlementServiceRawParameterClassifierSchema.shape,
    pub_key: z.string(),
    terminal_id: z.string(),
    payment: npAtobaraiPaymentSchema,
  })
  .passthrough();

export type NpAtobaraiSettlementServiceInputType = typeof npAtobaraiSettlementServiceParameterValidator._input;

/**
 * NP後払い決済サービスのサービス分類子。
 *
 * これを持ってして、どのサービスのパラメータかをまず判別する。
 * チェック項目以外の全パラメータの型情報を付与も附与されている。
 *
 * クライアント -> ModalDriverに対する入力は、コレを利用してサービスの分類の正しさのみ検証する。
 */
export const npAtobaraiSettlementServiceParameterClassifierSchema =
  npAtobaraiSettlementServiceRawParameterClassifierSchema as unknown as typeof npAtobaraiSettlementServiceParameterValidator;
